import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

const PluginMemoryDialog = ({
  open,
  onClose,
  memory,
  pluginName = '',
}) => {
  const { t } = useTranslation();

  const normalizedMemory = useMemo(() => {
    if (!memory || typeof memory !== 'object') {
      return null;
    }
    return memory;
  }, [memory]);

  const initialInput = useMemo(() => {
    if (!normalizedMemory) {
      return '';
    }
    const candidate = normalizedMemory.initial_input;
    if (typeof candidate === 'string') {
      return candidate.trim();
    }
    if (candidate === null || candidate === undefined) {
      return '';
    }
    return String(candidate).trim();
  }, [normalizedMemory]);

  const generatedPlan = useMemo(() => {
    if (!normalizedMemory || typeof normalizedMemory.generated_plan !== 'object' || normalizedMemory.generated_plan === null) {
      return null;
    }
    return normalizedMemory.generated_plan;
  }, [normalizedMemory]);

  const planSteps = useMemo(() => {
    if (!generatedPlan || !Array.isArray(generatedPlan.user_steps)) {
      return [];
    }
    return generatedPlan.user_steps
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }, [generatedPlan]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f6f8ff 100%)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <AutoAwesomeIcon sx={{ color: '#2563eb' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('generic_connector.memory_dialog_title', 'Plugin generation memory')}
            </Typography>
            {pluginName ? (
              <Typography variant="body2" color="text.secondary">
                {t('generic_connector.memory_dialog_plugin_label', 'Plugin')}: {pluginName}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <CloseButton onClick={onClose} />
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {!normalizedMemory && (
          <Typography variant="body2" color="text.secondary">
            {t('generic_connector.memory_dialog_empty', 'No memory information is available for this plugin.')}
          </Typography>
        )}

        {normalizedMemory && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('generic_connector.memory_dialog_initial_input', 'Initial user input')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {initialInput || t('generic_connector.memory_dialog_not_available', 'Not available')}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('generic_connector.memory_dialog_generated_plan', 'Generated plan')}
              </Typography>
              {generatedPlan ? (
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                  {generatedPlan.plan_title ? (
                    <Typography variant="body2">
                      <strong>{generatedPlan.plan_title}</strong>
                    </Typography>
                  ) : null}
                  {generatedPlan.summary ? (
                    <Typography variant="body2" color="text.secondary">
                      {generatedPlan.summary}
                    </Typography>
                  ) : null}
                  {planSteps.length > 0 ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {t('generic_connector.memory_dialog_user_steps', 'User steps')}
                      </Typography>
                      {planSteps.map((step, index) => (
                        <Typography key={`${step}-${index}`} variant="body2" color="text.secondary">
                          {index + 1}. {step}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('generic_connector.memory_dialog_not_available', 'Not available')}
                </Typography>
              )}
            </Box>

          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PluginMemoryDialog;
